import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components';
import { PathName } from 'appConstants';
import {
  eclipseDriveStarIcon,
  eclipseDriveTrophyIcon,
  eclipseDriveAmbassadorLicenseIcon,
  eclipseDriveHandshakeIcon,
  eclipseDriveCalendarIcon,
  eclipseDriveBulbIcon,
  eclipseDriveAirplaneIcon,
  eclipseDriveHotelIcon,
  eclipseDriveDinnerIcon,
  eclipseDriveResortImage,
  eclipseDriveShow1Image,
  eclipseDriveShow2Image,
  eclipseDriveShow3Image,
} from 'assets/images';
import { useSelector } from 'react-redux';
import { profileSelectors } from 'store/profile/selectors';
import styles from './styles.module.scss';

interface IFinalEventSectionProps {
  isAuthenticated: boolean;
}

const eventCards = [
  { icon: eclipseDriveStarIcon, alt: 'Top 10', textKey: 'results' },
  { icon: eclipseDriveTrophyIcon, alt: 'Winners', textKey: 'prizeDraw' },
  { icon: eclipseDriveAmbassadorLicenseIcon, alt: 'Licenses', textKey: 'ambassador' },
];

const benefitItems = [
  {
    icon: eclipseDriveHandshakeIcon, iconClass: 'benefit_icon', alt: 'Handshake', textKey: 'management', 
  },
  {
    icon: eclipseDriveBulbIcon, iconClass: 'bulb_icon', alt: 'Bulb', textKey: 'projects', 
  },
  {
    icon: eclipseDriveCalendarIcon, iconClass: 'calendar_icon', alt: 'Calendar', textKey: 'events', 
  },
  {
    icon: eclipseDriveAirplaneIcon, iconClass: 'airplane_icon', alt: 'Flight', textKey: 'flight', 
  },
  {
    icon: eclipseDriveHotelIcon, iconClass: 'benefit_icon', alt: 'Hotel', textKey: 'hotel', 
  },
  {
    icon: eclipseDriveDinnerIcon, iconClass: 'benefit_icon', alt: 'Dinner', textKey: 'dinner', 
  },
];

const galleryImages = [
  { src: eclipseDriveShow1Image, alt: 'Event showcase 1' },
  { src: eclipseDriveShow2Image, alt: 'Event showcase 2' },
  { src: eclipseDriveShow3Image, alt: 'Event showcase 3' },
];

export const FinalEventSection = ({ isAuthenticated }: IFinalEventSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('eclipseDrive');
  const { referralCode } = useSelector(profileSelectors.getState);

  return (
    <div className={styles.section}>
      <div className={styles.section_content}>
        <div className={styles.header}>
          <h2 className={styles.section_title}>{t('finalEvent.title')}</h2>
          <p className={styles.section_subtitle}>
            {t('finalEvent.subtitle')}
          </p>
        </div>
        
        <div className={styles.event_info}>
          {eventCards.map(({ icon, alt, textKey }) => (
            <div
              key={textKey}
              className={styles.event_card}
            >
              <img
                src={icon}
                alt={alt}
                className={styles.event_icon}
              />
              <div className={styles.event_value}>
                {t(`finalEvent.events.${textKey}`)}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.event_location}>
          <div className={styles.location_text}>
            <div className={styles.location_title}>{t('finalEvent.location')}</div>
            <div className={styles.location_date}>{t('finalEvent.date')}</div>
            <div className={styles.location_description}>
              {t('finalEvent.locationDescription')}
            </div>
          </div>
          <div className={styles.location_image_wrapper}>
            <img
              src={eclipseDriveResortImage}
              alt="Resort location"
              className={styles.resort_image}
            />
          </div>
        </div>
        
        <div className={styles.benefits_section}>
          <h3 className={styles.subsection_title}>
            {t('finalEvent.ambassadorBenefitsTitle')}
            <br />
            {t('finalEvent.ambassadorBenefitsTitleLine2')}
          </h3>
          
          <div className={styles.benefit_grid}>
            {benefitItems.map(({
              icon, iconClass, alt, textKey, 
            }) => (
              <div
                key={textKey}
                className={styles.benefit_item}
              >
                <div className={styles[iconClass]}>
                  <img
                    src={icon}
                    alt={alt}
                  />
                </div>
                <p className={styles.benefit_description}>
                  {t(`finalEvent.benefits.${textKey}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        <div className={styles.gallery}>
          {galleryImages.map(({ src, alt }) => (
            <div
              key={alt}
              className={styles.gallery_item}
            >
              <img
                src={src}
                alt={alt}
                className={styles.gallery_image}
              />
            </div>
          ))}
        </div>

        <p className={styles.closing_quote}>
          {t('finalEvent.closingQuote')}
        </p>
        
        {!isAuthenticated && (
          <div className={styles.cta_section}>
            <Button
              type="button"
              className={styles.cta_button}
              onClick={() => navigate(`${PathName.Registration}/?ref=${referralCode}`)}
            >
              {t('finalEvent.cta')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
